import {computed, observable, override} from "mobx";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {CompetitorPlayersStatsModuleTerms} from "../competitor-players-stats-module.terms";
import {CompetitorPlayersStatsModuleConfig} from "../competitor-players-stats-module.config";
import {COMPETITOR_PLAYER_STATS} from "../competitor-players-stats-module.consts";
import {CompetitorPlayersStatsModulePlayerViewModel} from "./competitor-players-stats-module.player.view-model";
import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {CompetitorPlayerStatsModuleCompetitorViewModel} from "./competitor-player-stats-module.competitor.view-model";
import {LINEUPS} from "../../../widgets/lineups-widget/lineups-widget.consts";
import {AthleteGameLineupsDataSource} from "../../../domain/athlete-game-lineups/athlete-game-lineups.data-source";
import {GameCenterLinkModel} from "../../../containers/game-center-container/models/game-center.link-model";
import {PartialGameDomainModel} from "../../../domain/partial-game/models/partial-game-domain-model";
import {ROUTE_NAMES} from "../../../core/consts";
import type {ChartEventDomainModel} from "../../../domain/full-game/models/chart-events/event/chart-event.domain-model";
import {ChartEventsViewModel} from "../../../domain/full-game/models/chart-events/chart-events.view-model";
import {ChartEventViewModel} from "../../../domain/full-game/models/chart-events/event/chart-event.view-model";
import {GameCardViewModel} from "../../../core/components/game-card/view-models/game-card.view-model";
import {AthleteTabViewModel} from "../../../widgets/athlete-widget/models/athlete-tab.view-model";
import {SPORT_TYPES} from "../../../core/app/consts";
import {CompetitorPlayerStatsModuleSportModel} from "./context/competitor-player-stats.module.sport-model";
import {CompetitorPlayerStatsModuleFootballModel} from "./context/competitor-player-stats.module.football-model";
import {CompetitorPlayerStatsModuleBasketballModel} from "./context/competitor-player-stats.module.basketball-model";
import {CompetitorPlayerStatsTabModel} from "./competitor-player-stats.tab.model";
import {LineupsMemberDomainModel} from "../../../domain/yard/lineups/lineups-member/lineups-member-domain-model";
import {GameCenterHashParamsModel} from "../../../containers/game-center-container/models/game-center.hash-params-model";

export class CompetitorPlayersStatsModuleState extends BaseModuleState {
  terms: CompetitorPlayersStatsModuleTerms;
  dataSource: AthleteGameLineupsDataSource = {};
  @observable selectedAthleteId: Number;
  @observable firstSelectedAthleteId: Number;
  @observable competitorId: Number;
  @observable activeTabIndex: Number;
  @observable matchWeekAthleteId: Number;
  @observable matchWeekGameId: Number;
  @observable isGoalViewExpanded: Boolean = false;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, CompetitorPlayersStatsModuleConfig];

  @override get isPopulated(): Boolean {
    return this.dataSource?.games && this.players && this.selectedPlayer;
  }

  @computed get game(): PartialGameDomainModel {
    let game = this.dataSource?.games?.get(this.selectedPlayer?.gameId);
    if (!game && this.dataSource?.games?.size === 1) {
      game = this.dataSource?.games.values().next().value;
    }
    return game;
  }

  @computed get gameStatus(): Number {
    const gameStatus: Number = this.game?.statusGroup

    return gameStatus;
  }

  @computed get gameLinkModel(): GameCenterLinkModel {
    const hashParams: GameCenterHashParamsModel = new GameCenterHashParamsModel({id: this.game?.id});
    const gameLinkModel: GameCenterLinkModel = new GameCenterLinkModel(this.game.matchupId, this.game.competition.sport.nameForURL, this.game.competition.nameForURL, this.game.nameForUrl, hashParams, this.game.isFollow);

    return gameLinkModel;
  }

  @computed get homeCompetitor(): CompetitorPlayerStatsModuleCompetitorViewModel {
    return new CompetitorPlayerStatsModuleCompetitorViewModel(this.game?.homeCompetitor);
  }

  @computed get awayCompetitor(): CompetitorPlayerStatsModuleCompetitorViewModel {
    return new CompetitorPlayerStatsModuleCompetitorViewModel(this.game?.awayCompetitor);
  }

  @computed get isHomeCompetitor(): Boolean {
    const isHomeCompetitor: Boolean = this.homeCompetitor.id === this.selectedCompetitor.domainModel.id;

    return isHomeCompetitor;
  }

  @computed get competitors(): Array<CompetitorPlayerStatsModuleCompetitorViewModel> {
    const competitors: CompetitorPlayerStatsModuleCompetitorViewModel = [this.homeCompetitor, this.awayCompetitor];

    return competitors;
  }

  @computed get chartEvents(): ChartEventsViewModel {
    const chartEvents: ChartEventsViewModel = this.dataSource?.chartEvents ? new ChartEventsViewModel(this.dataSource.chartEvents, this.device.isRtl, true) : null;

    return chartEvents;
  }

  @computed get isShallowShotMap(): Boolean {
    const isShallowShotMap = !this.chartEvents?.hasShotsCoordination;

    return isShallowShotMap;
  }

  @computed get sportTypeId(): Number {
    const SportTypeId: Number = this.appState.context.sportType.id

    return SportTypeId;
  }

  get isFootball(): Boolean {
    return this.sportTypeId === SPORT_TYPES.football
  }

  @computed get sportContext(): CompetitorPlayerStatsModuleSportModel {
    let type = null;

    switch (this.sportTypeId) {
      case SPORT_TYPES.football:
        type = CompetitorPlayerStatsModuleFootballModel;
        break;
      case SPORT_TYPES.basketball:
        type = CompetitorPlayerStatsModuleBasketballModel;
        break;
      default:
        type = CompetitorPlayerStatsModuleSportModel;
        break;
    }

    const sportContext: CompetitorPlayerStatsModuleSportModel = new type();

    return sportContext;
  }

  @computed get players(): Array<CompetitorPlayersStatsModulePlayerViewModel> {
    const validStatuses: Array<LINEUPS.MEMBERS_STATUS> = [LINEUPS.MEMBERS_STATUS.starting, LINEUPS.MEMBERS_STATUS.substitute];
    let players: Array<LineupsMemberDomainModel> = [];

    this.dataSource?.members?.forEach((member: LineupsMemberDomainModel, index: Number) => {
      const validPlayer: Boolean = validStatuses.includes(member.status) && !!member.athleteId;
      if (validPlayer) {
        const shots: Array<ChartEventDomainModel> = this.chartEvents?.events?.length > 0 ? this.chartEvents?.events
          .filter((chartEvent: ChartEventDomainModel) => chartEvent.playerId === member.id)
          .map((chartEvent: ChartEventDomainModel) => new ChartEventViewModel(chartEvent, index, this.device.isRtl, true)) : null;
        const playerVM: CompetitorPlayersStatsModulePlayerViewModel = new CompetitorPlayersStatsModulePlayerViewModel(member, this.dataSource.statsCategories, shots, this.appSettings.allowPremiumData);

        players.push(playerVM);
      }
    })

    return players;
  }

  @computed get playerIndex(): Number {
    const playerIndex: Number = this.players.findIndex((member: CompetitorPlayersStatsModulePlayerViewModel) => member.athleteId === this.selectedAthleteId)

    return playerIndex;
  }

  @computed get selectedPlayer(): CompetitorPlayersStatsModulePlayerViewModel {
    const selectedPlayer: CompetitorPlayersStatsModulePlayerViewModel = this.players[this.playerIndex];

    return selectedPlayer;
  }

  @computed get selectedCompetitor(): CompetitorPlayerStatsModuleCompetitorViewModel {
    const competitor: CompetitorPlayerStatsModuleCompetitorViewModel = this.selectedPlayer?.domainModel.competitorNum === this.game?.homeCompetitor.competitorNum ? this.homeCompetitor : this.awayCompetitor

    return competitor;
  }

  @computed get emptyStateImageWidth(): Number {
    const emptyStateImageWidth: Number = this.device.isSmallView ? COMPETITOR_PLAYER_STATS.DESIGN.EMPTY_STATE_IMAGE_SIZES.MOBILE.width : COMPETITOR_PLAYER_STATS.DESIGN.EMPTY_STATE_IMAGE_SIZES.DESKTOP.width;

    return emptyStateImageWidth;
  }

  @computed get emptyStateImageHeight(): Number {
    const emptyStateImageHeight: Number = this.device.isSmallView ? COMPETITOR_PLAYER_STATS.DESIGN.EMPTY_STATE_IMAGE_SIZES.MOBILE.height : COMPETITOR_PLAYER_STATS.DESIGN.EMPTY_STATE_IMAGE_SIZES.DESKTOP.height;

    return emptyStateImageHeight;
  }

  @computed get activeItemWidth(): Number {
    const activeItemWidth: Number = this.device.isSmallView ? COMPETITOR_PLAYER_STATS.DESIGN.PLAYER_LOGO_SIZE.mobile : COMPETITOR_PLAYER_STATS.DESIGN.PLAYER_LOGO_SIZE.desktop;

    return activeItemWidth;
  }

  @computed get shouldRenderStats(): Boolean {
    const shouldRenderStats: Boolean = this.selectedPlayer?.hasStats;

    return shouldRenderStats;
  }

  @computed get topStatsIconSize(): Number {
    const topStatsIconSize: Number = this.device.isSmallView ? COMPETITOR_PLAYER_STATS.DESIGN.TOP_STATS_ICON_SIZE.mobile : COMPETITOR_PLAYER_STATS.DESIGN.TOP_STATS_ICON_SIZE.desktop;

    return topStatsIconSize;
  }

  @computed get isFirstSelectedAthlete(): Boolean {
    const isFirstSelectedAthlete: Boolean = this.firstSelectedAthleteId === this.selectedAthleteId;

    return isFirstSelectedAthlete;
  }

  @computed get isSinglePlayer(): Boolean {
    return this.players.length === 1;
  }

  @computed get courtRatio(): Boolean {
    return this.sportContext.courtRatio;
  }

  @computed get courtType(): Boolean {
    return this.sportContext.courtType;
  }

  @computed get shouldRenderPlayerDetailsButton(): Boolean {
    return this.currentLinkModel.name !== ROUTE_NAMES.athlete && this.selectedPlayer?.enableAthleteLinkModel
  }

  @computed get shouldRenderGameDetailsButton(): Boolean {
    return this.currentLinkModel.name === ROUTE_NAMES.athlete
  }

  get gameViewModel(): GameCardViewModel {
    let gameViewModel: GameCardViewModel = null;
    if (this.game) {
      const date: String = this.datetimeUtils.toShortDate(this.game?.startTime);
      const time: String = this.datetimeUtils.toShortTime(this.game?.startTime);
      gameViewModel = new GameCardViewModel(this.game, this.terms, this.device.isSmallView, date, time, this.appState.currentOddsType);
      gameViewModel.hideRedCard = true;
    }
    return gameViewModel;
  }

  @computed get shouldRenderGameCard(): Boolean {
    return this.gameViewModel && this.props.showGameCard;
  }

  @computed get isInnerLinksBlocked(): Boolean {
    const isInnerLinksBlocked: Boolean = this.appState.settings.isWidget && this.appState.settings.bootstrapSettings.blockInnerLinks;

    return isInnerLinksBlocked;
  }

  @computed get shouldRenderGoalView(): Boolean {
    const validGoalViewSportTypes: Array<Number> = [SPORT_TYPES.football];
    const shouldRenderGoalView: Boolean = validGoalViewSportTypes.includes(this.game.sport.id);

    return shouldRenderGoalView;
  }

  @computed get shouldRenderTabs(): Boolean {
    const shouldRenderTabs: Boolean = this.tabs?.length > 1;

    return shouldRenderTabs;
  }

  @computed get shouldRenderShotMap() {
    return this.hasShotMap && (this.activeTabIndex === COMPETITOR_PLAYER_STATS.TABS.INDEXES.shotMap || !this.shouldRenderTabs);
  }

  @computed get shouldRenderHeatMap() {
    return this.hasHeatMap && (this.activeTabIndex === COMPETITOR_PLAYER_STATS.TABS.INDEXES.heatMap || !this.shouldRenderTabs);
  }

  @computed get hasHeatMap() {
    return this.selectedPlayer?.heatMap;
  }

  @computed get hasShotMap() {
    return this.selectedPlayer?.hasShotChart || this.selectedPlayer?.shots?.length > 0;
  }

  @computed get tabs(): Array<AthleteTabViewModel> {
    let tabs: Array<AthleteTabViewModel> = [];

    this.hasHeatMap && tabs.push(this.heatMapTab);
    this.hasShotMap && tabs.push(this.shotMapTab);

    return tabs;
  }

  @computed get heatMapTab(): CompetitorPlayerStatsTabModel {
    const heatMapTab: CompetitorPlayerStatsTabModel = new CompetitorPlayerStatsTabModel({
      index: COMPETITOR_PLAYER_STATS.TABS.INDEXES.heatMap,
      name: this.terms.WEB_PLAYER_POP_UP_HEATMAP,
      active: this.activeTabIndex === COMPETITOR_PLAYER_STATS.TABS.INDEXES.heatMap,
      analyticName: COMPETITOR_PLAYER_STATS.TABS.ANALYTIC_NAMES.heatMap
    });

    return heatMapTab;
  }

  @computed get shotMapTab(): CompetitorPlayerStatsTabModel {
    const shotMapTab: CompetitorPlayerStatsTabModel = new CompetitorPlayerStatsTabModel({
      index: COMPETITOR_PLAYER_STATS.TABS.INDEXES.shotMap,
      name: this.terms.WEB_PLAYER_POP_UP_SHOTMAP,
      active: this.activeTabIndex === COMPETITOR_PLAYER_STATS.TABS.INDEXES.shotMap,
      analyticName: COMPETITOR_PLAYER_STATS.TABS.ANALYTIC_NAMES.shotMap
    });

    return shotMapTab;
  }
}
